// General
import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getCookie, setCookie } from './utils/cookieUtils';
import ScrollToTop from './utils/ScrollToTop';

// amplify
import { Amplify } from 'aws-amplify';
import config from './aws-exports';
import '@aws-amplify/ui-react/styles.css';
import { AuthProvider, useAuth } from './Accounts/AuthContext';
import { NavbarProvider, useNavbar } from './pages/NavbarContext';
// CSS
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// ---- Eager: app chrome + student critical path ----------------------------
import NavigationBar from './pages/NavigationBar';
import StudentSideNav from './pages/StudentSideNav';
import { isMarketingRoute } from './pages/marketingRoutes';
// The home page ("/", also /teachers): the teacher connect story, the root
// since 2026-11-03 (the student landing held it from 2026-10-28).
import ConnectLanding from './Homepage/connect/ConnectLanding';
import SignIn from './Accounts/signin';
import SignUp from './Accounts/signup';
import VerificationCodeEntry from './Accounts/verificationCode';
import StudentHomeV3 from './StudentDashboardV3/StudentHomePage';
import ViewAsProvider, { ViewAsRouteGate } from './ViewAs/ViewAsProvider';
import StudentSubjectV3 from './StudentDashboardV3/StudentSubjectPage';
import StudentDomainV3 from './StudentDashboardV3/StudentDomainPage';
import HomeworkListV3 from './StudentDashboardV3/HomeworkListPage';
//Location pages (route data only - the page component itself is lazy)
import { locations } from './SEOLandingPages/locations';
import { HelmetProvider } from 'react-helmet-async';

// ---------------------------------------------------------------------------
// Route-level code splitting (2026-08-17, Chromebook perf pass).
//
// EAGER (imported statically below): the app shell, the student marketing
// landing, sign-in/up, and the four StudentDashboardV3 nav pages - the
// student critical path. Everything else is lazy so a 4GB Chromebook never
// downloads teacher/admin/legacy code.
//
// PREFETCH (webpackPrefetch: true): the session player, homework player and
// diagnostics - fetched by the browser at idle right after first paint, so
// students never see a loading state when they enter a session, without the
// chunks (KaTeX lives here) bloating first paint.
//
// lazyRetry: after a deploy the running tab may hold an index.html whose
// chunk hashes were replaced. One hard reload fetches the new build instead
// of surfacing a ChunkLoadError to the student.
// ---------------------------------------------------------------------------
const lazyRetry = (importFn) => lazy(() =>
  importFn().then((m) => {
    sessionStorage.removeItem('chunk-reload');
    return m;
  }).catch((err) => {
    if (!sessionStorage.getItem('chunk-reload')) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
      // Return a never-resolving module so nothing renders mid-reload.
      return new Promise(() => {});
    }
    throw err;
  })
);

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-label="Loading page">
      <div className="route-fallback-spinner" />
    </div>
  );
}

// ---- Prefetched: student work surfaces (KaTeX lives in these chunks) ------
const HomeworkPlayerV3 = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDashboardV3/HomeworkPlayerPage'));
const PlacementWalkV3 = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDashboardV3/PlacementWalkPage'));
const ReadingSessionPage = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDash/Reading/ReadingSessionPage'));
const DomainStartPage = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDash/Learn/DomainStartPage'));
const SubjectChoicePage = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDash/SubjectChoicePage'));
//Diagnostic
const DiagnosticTest = lazyRetry(() => import(/* webpackPrefetch: true */ './Diagnostic/DiagnosticTestIntroPage'));
const DiagnosticTestQuestionPage = lazyRetry(() => import(/* webpackPrefetch: true */ './Diagnostic/DiagnosticTestQuestionPage'));
const DiagnosticTestScorePage = lazyRetry(() => import(/* webpackPrefetch: true */ './Diagnostic/DiagnosticScorePage'));
// The score story: Buffy walks a student through their finished diagnostic
// (replaced the old PostDiagnosticJourney 2026-10-02).
const ScoreStoryPage = lazyRetry(() => import(/* webpackPrefetch: true */ './StudentDash/PostDiagnostic/ScoreStoryPage'));
const ScoreReportPage = lazyRetry(() => import('./StudentDash/PostDiagnostic/report/ScoreReportPage'));
//DiagnosticMini
const IntroPage = lazyRetry(() => import(/* webpackPrefetch: true */ './DiagnosticMini/IntroPage'));
const QuestionPage = lazyRetry(() => import(/* webpackPrefetch: true */ './DiagnosticMini/QuestionPage'));
const DiagnosticMiniResultsPage = lazyRetry(() => import(/* webpackPrefetch: true */ './DiagnosticMini/DiagnosticMiniResultsPage'));

// ---- Lazy: marketing / misc frontend pages --------------------------------
// The landing redos are the live pages (Sandy 2026-10-28): /teachers is the
// connect story (2026-09-29) with its start survey + teacher signup at
// /teachers/start (ConnectLanding, imported above, eager because it is the
// root since 2026-11-03); /for-schools the schools landing (2026-09-30);
// /students the student landing; /testimonials and /contact the 2026-10-12
// redos; /join the class-code student signup (2026-10-27). The old
// "-preview" paths redirect to them.
const StudentsPreview = lazyRetry(() => import('./Homepage/students/StudentsPreview'));
const ConnectMomentGallery = process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./Homepage/connect/moments/MomentGallery'))
  : null;
const TeacherSurvey = lazyRetry(() => import('./Homepage/connect/survey/TeacherSurvey'));
const JoinPage = lazyRetry(() => import('./Join/JoinPage'));
const SchoolsLanding = lazyRetry(() => import('./Homepage/schools/SchoolsLanding'));
const TestimonialsPreview = lazyRetry(() => import('./Homepage/testimonials/TestimonialsPreview'));
const ContactPreview = lazyRetry(() => import('./Homepage/contact/ContactPreview'));
// import Homepage2 from './Homepage/Homepage2';
const FAQ = lazyRetry(() => import('./pages/FAQ'));
const Return = lazyRetry(() => import('./pages/PaymentPage').then(m => ({ default: m.Return })));
const CheckoutPage = lazyRetry(() => import('./Start/CheckoutPage'));
const StartFlow = lazyRetry(() => import('./Start/StartFlow'));
const StartSignup = lazyRetry(() => import('./Start/StartSignup'));
// Onboarding page removed (Sandy 2026-08-09) - dashboard tours replaced it.
const Success = lazyRetry(() => import('./pages/Success'));
const LoadingSpinnerPage = lazyRetry(() => import('./pages/LoadingSpinnerPage'));
const AboutUs = lazyRetry(() => import('./pages/AboutUs'));
const IndividualStudentLandingPage = lazyRetry(() => import('./pages/IndividualStudentLandingPage'));
const LocationPage = lazyRetry(() => import('./SEOLandingPages/LocationPage'));
const PolicyDocument = lazyRetry(() => import('./pages/PolicyDocument'));

//School pages for group classes
const Develyn = lazyRetry(() => import('./Schools/Develyn'));
const Horizon = lazyRetry(() => import('./Schools/Horizon'));
const SchoolLandingPage = lazyRetry(() => import('./Schools/SchoolLandingPage'));
// BearCreek / ArvadaWest / Chatfield: routes have been commented out for a
// while - imports parked with them (2026-08-17).
// import BearCreek from './Schools/BearCreek';
// import ArvadaWest from './Schools/ArvadaWest';
// import Chatfield from './Schools/Chatfield';

// ---- Lazy: homework + accounts --------------------------------------------
const StudentLiveSession = lazyRetry(() => import('./Homework/StudentLiveSession.jsx'));
const Homework = lazyRetry(() => import('./Homework/Homework'));
const AssignmentLauncher = lazyRetry(() => import('./Homework/AssignmentLauncher'));
const FormativeAssessmentPractice = lazyRetry(() => import('./Homework/FormativeAssessmentPractice'));
const FormativeAssessmentResults = lazyRetry(() => import('./Homework/FormativeAssessmentResults'));
const AccountManagement = lazyRetry(() => import('./Accounts/AccountManagement'));
const ResetPassword = lazyRetry(() => import('./Accounts/ResetPassword'));
const HomeworkAnswered = lazyRetry(() => import('./Homework/HomeworkAnswered'));
const FreindsDashboard = lazyRetry(() => import('./Accounts/Socials/FriendsDashboard'));
const FriendProfile = lazyRetry(() => import('./Accounts/Socials/FriendProfile'));
// affiliate page (route long-commented; import parked 2026-08-17)
// import AffiliateProgram from "./Accounts/AccountManagementComponents/AffiliateProgram";

// ---- Lazy: dashboards ------------------------------------------------------
// Legacy TeacherDashboard2.0 wrapper: route parked 2026-08-09, import parked
// 2026-08-17 with the perf pass.
// import TeacherDashboardWrapper from './TeacherDashboard2.0/TeacherDashboardWrapper';
// TeacherQuestions route removed from the bundle 2026-08-17 (legacy 2.0 tool;
// re-enable by uncommenting this import and its route below).
// const TeacherQuestions = lazyRetry(() => import('./TeacherDashboard2.0/TeacherDashboardComponents/Views/TeacherQuestions'));
// The 2.0 admin dashboard stays routed at /admin-dashboard-legacy (direct URL only).
const AdminDashboard = lazyRetry(() => import('./TeacherDashboard2.0/AdminDashboard'));
// Teacher Dashboard v3
// Home; the demo board behind it is development-only (HomeRoute.jsx, audit F5).
const HomeRouteV3 = lazyRetry(() => import('./TeacherDashboardV3/HomeRoute'));
const MonitorPageV3 = lazyRetry(() => import('./TeacherDashboardV3/MonitorPage'));
const DiagnosticPageV3 = lazyRetry(() => import('./TeacherDashboardV3/DiagnosticPage'));
const DiagnosticGuidePageV3 = lazyRetry(() => import('./TeacherDashboardV3/DiagnosticGuidePage'));
// dev only: the teacher Diagnostic page on a made-up class (guide screenshots)
const DiagnosticDemoPageV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/DiagnosticDemoPage')) : null;
// Diagnostic review for teachers (2026-10-10; shipped 2026-10-26): results and
// a colleague's PLC link. The made-up demo stays development-only.
const DiagnosticReviewV3 = lazyRetry(() => import('./TeacherDashboardV3/diagnosticReview/DiagnosticReviewPage'));
const DiagnosticReviewDemoV3 = process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./TeacherDashboardV3/diagnosticReview/DiagnosticReviewPage').then((m) => ({ default: m.DemoReviewPage })))
  : null;
const DiagnosticReviewSharedV3 = lazyRetry(() => import('./TeacherDashboardV3/diagnosticReview/DiagnosticReviewPage').then((m) => ({ default: m.SharedReviewPage })));
const IntervenePageV3 = lazyRetry(() => import('./TeacherDashboardV3/IntervenePage'));
const AssignmentsPageV3 = lazyRetry(() => import('./TeacherDashboardV3/AssignmentsPage'));
const GradebookPageV3 = lazyRetry(() => import('./TeacherDashboardV3/GradebookPage'));
const StudentsPageV3 = lazyRetry(() => import('./TeacherDashboardV3/StudentsPage'));
// Curriculum Connect (2026-09-25). Release flag (2026-10-26): on in development,
// and in a production build when .env.production says REACT_APP_CURRICULUM_CONNECT=1
// (TeacherDashboardV3/releaseFlags.js). The expression stays inline so webpack
// can drop the import() and keep the page out of the bundle until then.
const CurriculumConnectPageV3 = process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./TeacherDashboardV3/CurriculumConnectPage'))
  : null;
// Curriculum setup onboarding (2026-10-10): the same release flag.
const CurriculumSetupPageV3 = process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./TeacherDashboardV3/curriculum/setup/CurriculumSetupPage'))
  : null;
// The printable PLC plan (2026-10-10): same localhost-only guard.
const PlcPlanPageV3 = process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./TeacherDashboardV3/curriculum/PlcPlanPage'))
  : null;
// Question Lab (2026-10-17): renders curriculum-question JSON through the real homework QuestionCard, LOCALHOST ONLY.
const QuestionLabPage = process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./qa/QuestionLab'))
  : null;
// Curriculum Connect intake + review lab (2026-10-18, stage 2a): same localhost-only guard.
const CurriculumIntakeLabV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/curriculum/intake/IntakeLab')) : null;
// Class context lab (2026-10-25, stage 1): localhost only, like the intake lab.
const ClassContextLabV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/curriculum/context/ContextLab')) : null;
// PLCs (2026-10-27; shipped with REACT_APP_PLCS=1, 2026-10-28): the condition is repeated here so webpack drops the page when it is off (releaseFlags PLCS)
const PlcsPageV3 = (process.env.REACT_APP_PLCS === '1' || process.env.NODE_ENV === 'development') ? lazyRetry(() => import('./TeacherDashboardV3/plcs/PlcsPage')) : null;
// Assessments tab (2026-10-18, stage A1): the same release flag as Curriculum Connect.
const AssessmentsPageV3 = (process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') ? lazyRetry(() => import('./TeacherDashboardV3/assessments/AssessmentsPage')) : null;
const AssessmentsLabV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/assessments/AssessmentsLab')) : null;
// The Assessments flow (stage 1, 2026-10-21): the same release flag.
const AssessmentsFlowV3 = (process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') ? lazyRetry(() => import('./TeacherDashboardV3/assessments/flow/AssessmentsFlowPage')) : null;
// Explainer videos (2026-10-12): the /videos/:videoId player, LOCALHOST ONLY.
const ExplainerVideoPage = process.env.NODE_ENV === 'development'
  ? lazyRetry(() => import('./ExplainerVideos/VideoPage'))
  : null;
// Insights merged into Home (Sandy 2026-08-23); old URL redirects below.
const GuidePageV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/GuidePage')) : null;
const MissedQuestionsPageV3 = lazyRetry(() => import('./TeacherDashboardV3/MissedQuestionsPage'));
// Loop lab (Stage A isolation build of the adaptive-home loop viz; temporary)
const LoopLabV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/LoopLab')) : null;
// Admin Dashboard v3 (school administrator view)
const AdminOverviewV3 = lazyRetry(() => import('./AdminDashboardV3/OverviewPage'));
const AdminGoalsV3 = lazyRetry(() => import('./AdminDashboardV3/GoalsPage'));
const AdminResultsV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./AdminDashboardV3/ResultsPage')) : null;
const AdminDiagnosticsV3 = lazyRetry(() => import('./AdminDashboardV3/DiagnosticsPage'));
// One report per diagnostic for the school (2026-10-07, development only until stage 2).
const AdminDiagnosticResultsV3 = lazyRetry(() => import('./AdminDashboardV3/diagnosticResults/DiagnosticResultsPage'));
const AdminReportV3 = lazyRetry(() => import('./AdminDashboardV3/ReportPage'));
const AdminGuideV3 = lazyRetry(() => import('./AdminDashboardV3/GuidePage'));
// Admin directory pages (2026-09-25, admin clarity overhaul)
const AdminStudentsV3 = lazyRetry(() => import('./AdminDashboardV3/directory/StudentsPage'));
const AdminStudentV3 = lazyRetry(() => import('./AdminDashboardV3/directory/StudentDetailPage'));
const AdminTeachersV3 = lazyRetry(() => import('./AdminDashboardV3/directory/TeachersPage'));
const AdminTeacherV3 = lazyRetry(() => import('./AdminDashboardV3/directory/TeacherDetailPage'));
const AdminClassV3 = lazyRetry(() => import('./AdminDashboardV3/directory/ClassDetailPage'));
const AdminLevelsV3 = lazyRetry(() => import('./AdminDashboardV3/directory/LevelsPage'));
const AdminUsageV3 = lazyRetry(() => import('./AdminDashboardV3/directory/UsagePage'));
const AdminAccommodationsV3 = lazyRetry(() => import('./AdminDashboardV3/directory/AccommodationsPage'));
// District Dashboard v3 (district administrator view)
const DistrictOverviewV3 = lazyRetry(() => import('./AdminDashboardV3/district/DistrictOverviewPage'));
const DistrictSchoolsV3 = lazyRetry(() => import('./AdminDashboardV3/district/DistrictSchoolsPage'));
const DistrictResultsV3 = lazyRetry(() => import('./AdminDashboardV3/district/DistrictResultsPage'));
const DistrictReportV3 = lazyRetry(() => import('./AdminDashboardV3/district/DistrictReportPage'));
const DistrictGuideV3 = lazyRetry(() => import('./AdminDashboardV3/district/DistrictGuidePage'));

// Legacy StudentPracticeDashboard (chart.js) - routes removed from the bundle
// 2026-08-17: only reachable from the parked CurriculumDashboard, orphaned in
// the live UI. Re-enable by uncommenting these imports and their routes.
// const StudentDashboard = lazyRetry(() => import('./StudentPracticeDashboard/StudentDashboardSimple'));
// const ViewPreviousAssignments = lazyRetry(() => import('./StudentPracticeDashboard/StudentDashboardComponents/ViewPreviousAssignments'));
// const ViewAssignmentDetails = lazyRetry(() => import('./StudentPracticeDashboard/StudentDashboardComponents/ViewPreviousSet'));
// const StudentDetailedAnalytics = lazyRetry(() => import('./StudentPracticeDashboard/StudentDashboardComponents/StudentDetailedAnalyticsCopy'));

// ---- Lazy: StudentDash (lesson engine pages outside the prefetched player) --
const StudentDash = lazyRetry(() => import('./StudentDash/StudentDash'));
const PracticeQuestionPage = lazyRetry(() => import('./StudentDash/Practice/PracticeQuestionPage'));
const ConceptLessonPage = lazyRetry(() => import('./StudentDash/Learn/ConceptLessonPage'));
const DomainMetaPage = lazyRetry(() => import('./StudentDash/Learn/DomainMetaPage'));
const QABrowserPage = lazyRetry(() => import('./StudentDash/QA/QABrowserPage'));
const QALessonReviewPage = lazyRetry(() => import('./StudentDash/QA/QALessonReviewPage'));
// Internal tools and prototypes: LOCALHOST ONLY (Sandy 2026-10-13: nothing
// links to them, so they must not be reachable on the live site by URL).
// Same guard on LoopLabV3, GuidePageV3, DiagnosticDemoPageV3, AdminResultsV3.
const QAPlacementPage = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./StudentDashboardV3/QAPlacementPage')) : null;
const DiagnosticQAPage = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./Diagnostic/DiagnosticQAPage')) : null;
// Dev-only Buffy clip lab (renders nothing in production builds).
const BuffyLab = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./components/buffy/BuffyLab')) : null;
// Dev-only gallery of everything the teacher's Buffy chats can show, on fake data.
const BuffyGalleryV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/gallery/BuffyGallery')) : null;
const InlineTextGalleryV3 = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./TeacherDashboardV3/gallery/InlineTextGallery')) : null;
const DiagnosticsPreview = process.env.NODE_ENV === 'development' ? lazyRetry(() => import('./Diagnostic/DiagnosticsPreview')) : null;
const LessonPreviewPage = lazyRetry(() => import('./StudentDash/Reading/LessonPreviewPage'));
// Temporary backend-free previews of the new Learn lesson UX (mockup mode) -
// routes removed from the bundle 2026-08-17. Re-enable by uncommenting.
// const TransitionsLesson = lazyRetry(() => import('./StudentDash/Learn/concepts/Transitions'));
// const RhetoricalSynthesisLesson = lazyRetry(() => import('./StudentDash/Learn/concepts/RhetoricalSynthesis'));

// teacher preview of legacy student pages - routes removed from the bundle
// 2026-08-17 (they previewed the pre-V3 student surfaces). Re-enable by
// uncommenting these imports and their routes below.
// const PreviewCurriculumDashboard = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewCurriculum'));
// const PreviewTeacherAssignments = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewTeacherAssignments'));
// const PreviewStudentDetailedAnalyticsCopy = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewStudentAnalyticsCopy'));
// const PreviewDiagnosticTest = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewDiagnosticTest'));
// const PreviewHomework = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewHomework'));
// const PreviewLearningPage = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewLearningPage'));
// const PreviewHomeworkAnswered = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewHomeworkAnswered'));
// const PreviewStudentDashboard = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewStudentDashboard'));
// const PreviewLandingPage = lazyRetry(() => import('./TeacherDashboard2.0/StudentPreview/PreviewLandingPage'));

//concept definition
const ConceptDefOverview = lazyRetry(() => import('./pages/ConceptDefOverview'));
const ModulesPage = lazyRetry(() => import('./pages/ConceptDefOverviewComponents/ModulesPage'));

// internal tool
const InternalDashboard = lazyRetry(() => import('./pages/InternalDashboard'));
// dev-only content authoring workbench (backed by the local /workbench-api)
const ContentWorkbench = lazyRetry(() => import('./pages/ContentWorkbench/ContentWorkbench'));

const LandingPage = lazyRetry(() => import('./Learn/landingPage'));
const LearningPage = lazyRetry(() => import('./Learn/LearningPage'));

const AdminSurvey = lazyRetry(() => import('./pages/AdminSurvey'));

// Legacy student dashboard - route parked 2026-08-09, import parked 2026-08-17.
// import CurriculumDashboard from './CurriculumDashboard/CurriculumDashboard';

// College Explorer (behind login)
const CollegeBrowser = lazyRetry(() => import('./Colleges/CollegeBrowser'));
const CollegeDetail = lazyRetry(() => import('./Colleges/CollegeDetail'));
// District Scores explorer (public marketing surface)
const DistrictScoresSearch = lazyRetry(() => import('./CoScores/DistrictScoresSearch'));
const DistrictScoresPage = lazyRetry(() => import('./CoScores/DistrictScoresPage'));
const DemoTour = lazyRetry(() => import('./CoScores/demo/DemoTour'));
const SATBluebookUpload = lazyRetry(() => import('./pages/Bluebook/SATBluebookUpload'));
const SATBluebookResults = lazyRetry(() => import('./pages/Bluebook/SATBluebookResults'));
const PsatScoreReport = lazyRetry(() => import('./pages/Bluebook/PsatScoreReport'));
Amplify.configure(config);

// App chrome (Sandy 2026-07-24, reworked 2026-07-27) — who gets what:
//   Signed in, anywhere in the app: the left rail, never the top bar. That is
//     every role now — students, teachers and admins alike. Teachers used to
//     get the top bar on top of the dashboard's own rail, which was two navs
//     stacked on the same screen.
//   Marketing routes: always the top NavigationBar, whoever is signed in, so a
//     public pitch page never wears app chrome — see src/pages/marketingRoutes.js.
//   Logged out: the top bar, as before.
//   Routes that ship their own chrome (the V3 teacher dashboard) get NEITHER,
//     because DashboardShell already renders a rail — see BRINGS_OWN_CHROME.
//   hideNavbar() from NavbarContext (workbench, wbpreview, diagnostic test)
//     still hides everything.
// This lives inside <Router> because the decision needs the current route.

// Surfaces that render their own navigation and must not be given app chrome
// on top of it. Prefix match: every /teacher-dashboard/* page is a
// DashboardShell page, and DashboardShell draws its own 76px rail. The admin
// and district V3 dashboards are AdminShell pages — same rail, same rule.
// /teachers/start: the teacher start survey draws its own top bar (2026-09-30).
// /join, /signin and /signup draw the same top bar (2026-10-28).
// /post-diagnostic: the score story is a full-screen moment with its own top bar.
// /qa/diagnostics (dev only) draws the student rail itself, signed out.
// /videos (dev only): the explainer video player is full-screen, no site nav.
const BRINGS_OWN_CHROME = ['/teacher-dashboard', '/teacher-dashboard-demo', '/admin-dashboard', '/district-dashboard-v3', '/teachers/start', '/join', '/signin', '/signup', '/post-diagnostic', '/qa/diagnostics', '/videos'];
const bringsOwnChrome = (pathname) =>
  BRINGS_OWN_CHROME.some((p) => pathname === p || pathname.startsWith(`${p}/`));

function AppChrome({ children }) {
  const { isNavbarVisible } = useNavbar();
  const { user } = useAuth() || {};
  const location = useLocation();

  // Same rule NavigationBar uses: the audit preview reads as a student no
  // matter who is signed in (or whether anyone is).
  const isAuditPreview = location.pathname.startsWith('/session/')
    && new URLSearchParams(location.search).get('audit') === '1';
  const isSignedInAppUser = !!user?.role;
  // Contact is a marketing page, but a signed-in student opens it from their
  // own rail ("Contact"), so it keeps the rail (Sandy 2026-10-13).
  const studentContact = user?.role === 'Student' && location.pathname === '/contact';
  const wantsSideRail = (isSignedInAppUser || isAuditPreview)
    && (!isMarketingRoute(location.pathname) || studentContact);

  const ownChrome = bringsOwnChrome(location.pathname);
  const showSideNav = isNavbarVisible && wantsSideRail && !ownChrome;
  const showTopNav = isNavbarVisible && !wantsSideRail && !ownChrome;

  // Phones show the top bar on every marketing page, the home page too
  // (Sandy 2026-10-28). Until then the old student landing's phone hero hid
  // it on / and /students; the new home page has no phone hero of its own.
  const hideTopNavOnPhone = false;

  return (
    // Phones stack the student chrome (top bar + dropdown) above the page;
    // md+ keeps the side-by-side rail layout (Sandy 2026-08-18).
    <div className={`App min-vh-100 d-flex ${showSideNav ? 'flex-column flex-md-row' : 'flex-column'}`}>
      {showTopNav && (
        /* No background and no pointer events on the wrapper; the nav
           paints its own bar and re-enables its own pointer events. */
        <header
          className={`position-fixed w-100 ${hideTopNavOnPhone ? 'hidden sm:block' : ''}`}
          style={{ zIndex: 1000, pointerEvents: 'none' }}
        >
          <NavigationBar />
        </header>
      )}
      {showSideNav && <StudentSideNav />}
      {/* min-w-0 next to the rail so wide content scrolls inside the column
          instead of pushing the page sideways (Chromebooks at 1366px). */}
      {/* mt-16 (64px, a Tailwind-only name Bootstrap can't hijack) matches the
          floating nav's rest height (py-3 + h-10 logo = 64px). The old mt-5
          was Bootstrap's 48px, which left the first 16-24px of every page
          hidden under the fixed header — on phones the hero headline rendered
          straight into the logo. */}
      {/* When the phone hides the nav, the 64px offset goes with it — the
          landing's phone hero owns the very top of the screen. */}
      <main className={`flex-grow-1 ${showSideNav ? 'min-w-0' : ''} ${showTopNav ? (hideTopNavOnPhone ? 'sm:mt-16' : 'mt-16') : ''}`}>
        {children}
      </main>
    </div>
  );
}

function AppContent() {
  return (
    <Router>
      {/* Admin "view as" (2026-09-25): re-provides auth as the viewed teacher
          or student and draws the view-mode bar; a no-op otherwise. */}
      <ViewAsProvider>
      <AppChrome>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
          <ViewAsRouteGate>
          <Routes>
            {/* Add location home pages */}
            {locations.map(locationData => (
              <Route
                key={locationData.slug}
                path={`/${locationData.slug}`}
                element={<LocationPage locationData={locationData} />}
              />
            ))}
            <Route path="/contact" element={<ContactPreview />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="about" element={<AboutUs />} />
            <Route path="/start" element={<StartFlow />} />
            {/* The quiz flow's slim signup (self-serve students only) —
                school-cohort signups keep using /signup (Sandy 2026-08-27) */}
            <Route path="/start/signup" element={<StartSignup />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/return" element={<Return />} />
            <Route path="/homework" element={<Homework />} />
            <Route path="/assignment/:assignmentId" element={<AssignmentLauncher />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/success" element={<Success />} />
            <Route path="/signup" element={<SignUp />} />
            {/* /SuccessfulSignOut interstitial removed (Sandy 2026-08-14):
                logout goes straight to the landing page. */}
            <Route path="/SuccessfulSignOut" element={<Navigate to="/" replace />} />
            <Route path="/verificationCode" element={<VerificationCodeEntry />} />
            <Route path="/manageAccount" element={<AccountManagement />} />
            <Route path="/resetPassword" element={<ResetPassword />} />
            <Route path="/homework-answered" element={<HomeworkAnswered />} />

            {/* The old Teacher Program signup and its page lead to the new teacher signup (2026-10-28). */}
            <Route path="/teacherprogram/signup" element={<Navigate to="/teachers/start" replace />} />
            <Route path="/teacherprogram/about" element={<Navigate to="/teachers" replace />} />
            <Route path="/testimonials" element={<TestimonialsPreview />} />
            <Route path="/individual-student-landing-page" element={<IndividualStudentLandingPage />} />

            <Route path='/schools' element={<SchoolLandingPage />} />
            <Route path="/develyn" element={<Develyn />} />
            <Route path='/horizon' element={<Horizon />} />
            {/*
            <Route path="/bearcreek" element={<BearCreek />} />
            <Route path='/arvadawest' element={<ArvadaWest />} />
            <Route path='/chatfield' element={<Chatfield />} />

            */}

            <Route path="/student/live-session" element={<StudentLiveSession />} />

            {/* URL swap (Sandy 2026-08-09): /student-dashboard is now the V3
                dashboard (below); the legacy CurriculumDashboard route is
                parked here for reference. */}
            {/* <Route path="/student-dashboard" element={<CurriculumDashboard />} /> */}
            <Route path="/questionnaireResults" element={<AdminSurvey />} />

            {/* Legacy StudentPracticeDashboard routes removed from the bundle
                (perf pass 2026-08-17) - they were only linked from the parked
                CurriculumDashboard. Re-enable with their imports above. */}
            {/* <Route path="/practice-dashboard" element={<StudentDashboard />} /> */}
            {/* <Route path="/view-previous-practice" element={<ViewPreviousAssignments />} /> */}
            {/* <Route path="/previous-assignments/:setId" element={<ViewAssignmentDetails />} /> */}

            <Route path="/StudentDash" element={<StudentDash />} />
            <Route path="/practice/:mainConcept" element={<PracticeQuestionPage />} />
            {/* Subject picker → English routes into the live domain; Math grayed out. */}
            <Route path="/subjects" element={<SubjectChoicePage />} />
            {/* Adaptive domain entry — "<Domain> — Start". */}
            <Route path="/domain/:domainId" element={<DomainStartPage />} />
            {/* The phase-driven reading band session (review → lesson → practice → mixed → skill-check). */}
            <Route path="/session/:domainId" element={<ReadingSessionPage />} />
            {/* Internal QA content browser (Teacher/Admin only) — browse/test v2 questions & lessons. */}
            <Route path="/qa" element={<QABrowserPage />} />
            {/* Lesson review workflow — walk every lesson/spot/practice with a local checked ledger. */}
            <Route path="/qa/lessons" element={<QALessonReviewPage />} />
            {/* Placement clone render review — needs local gitignored placement-qa-clones.json. */}
            {process.env.NODE_ENV === 'development' && <Route path="/qa/placement" element={<QAPlacementPage />} />}
            {/* Full-diagnostic render review — needs local gitignored diagnostic-qa-items.json. */}
            {process.env.NODE_ENV === 'development' && <Route path="/qa/diagnostic" element={<DiagnosticQAPage />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/qa/buffy" element={<BuffyLab />} />}
            {/* Dev only: render curriculum-question JSON (scratch/2026-10-17-cb-harvest/pilot) through the homework player's card. */}
            {process.env.NODE_ENV === 'development' && <Route path="/qa/question-lab" element={<QuestionLabPage />} />}
            {/* Dev only: the diagnostics page in every state (no account needed). */}
            {process.env.NODE_ENV === 'development' && <Route path="/qa/diagnostics" element={<DiagnosticsPreview />} />}
            {/* Dev preview of any built interactive lesson, outside a session. */}
            <Route path="/lesson-preview" element={<LessonPreviewPage />} />
            <Route path="/lesson-preview/:concept/:band" element={<LessonPreviewPage />} />


            <Route path="/learn-landing-page" element={<LandingPage />} />
            <Route path="/learn/video" element={<LearningPage />} />
            {/* Backwards-compat alias for the old hardcoded URL. */}
            <Route
              path="/learn/student-note-questions"
              element={<ConceptLessonPage mainConcept="rhetorical-synthesis" />}
            />
            {/* Domain meta lesson (Section 1) — must come before the generic concept route. */}
            <Route path="/learn/domain/:domainId" element={<DomainMetaPage />} />
            {/* Generic concept-driven lesson route. */}
            <Route path="/learn/:mainConcept" element={<ConceptLessonPage />} />
            {/* Temporary backend-free previews of the new Learn UX - routes
                removed from the bundle (perf pass 2026-08-17). */}
            {/* <Route path="/learn-preview/transitions" element={<TransitionsLesson />} /> */}
            {/* <Route path="/learn-preview/rhetorical-synthesis" element={<RhetoricalSynthesisLesson />} /> */}

            <Route path="/diagnostic" element={<DiagnosticTest />} />
            <Route path="/diagnostic/questions/:assignmentId" element={<DiagnosticTestQuestionPage />} />
            <Route path="/diagnostic/score" element={<DiagnosticTestScorePage />} />
            <Route path="/post-diagnostic" element={<ScoreStoryPage />} />
            <Route path="/post-diagnostic/report" element={<ScoreReportPage />} />

            {/* URL swap (Sandy 2026-08-09): /teacher-dashboard is now the V3
                dashboard (below); the legacy TeacherDashboard2.0 route is
                parked here for reference. */}
            {/* <Route path="/teacher-dashboard" element={<TeacherDashboardWrapper />} /> */}
            {/* Legacy 2.0 Questions tool removed from the bundle (perf pass
                2026-08-17). Re-enable with its import above. */}
            {/* <Route path="/teacher-dashboard/Questions" element={<TeacherQuestions />} /> */}
            {/* The 2.0 admin dashboard (real accounts, diagnostic window controls)
                moved here 2026-08-16 when the V3 dashboard took /admin-dashboard.
                Since 2026-09-02 every Admin redirect (sign-in, nav, side nav)
                lands on /admin-dashboard; this stays reachable by URL only. */}
            <Route path="/admin-dashboard-legacy" element={<AdminDashboard />} />

            <Route path="/teacher-dashboard" element={<HomeRouteV3 />} />
            {/* Demo twin: the August task board with the demo story (development only, audit F5). */}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard-demo" element={<HomeRouteV3 />} />}
            <Route path="/teacher-dashboard/monitor" element={<MonitorPageV3 />} />
            <Route path="/teacher-dashboard/diagnostic" element={<DiagnosticPageV3 />} />
            <Route path="/teacher-dashboard/diagnostic/guide" element={<DiagnosticGuidePageV3 />} />
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/diagnostic/demo" element={<DiagnosticDemoPageV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/buffy-gallery" element={<BuffyGalleryV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/buffy-inline" element={<InlineTextGalleryV3 />} />}
            <Route path="/teacher-dashboard/diagnostic/results" element={<DiagnosticReviewV3 />} />
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/diagnostic/results/demo" element={<DiagnosticReviewDemoV3 />} />}
            <Route path="/teacher-dashboard/diagnostic/shared/:shareId" element={<DiagnosticReviewSharedV3 />} />
            <Route path="/teacher-dashboard/intervene" element={<IntervenePageV3 />} />
            <Route path="/teacher-dashboard/assignments" element={<AssignmentsPageV3 />} />
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/assessments" element={<AssessmentsPageV3 />} />}
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/assessments/new" element={<AssessmentsPageV3 />} />}
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/assessments/:draftId" element={<AssessmentsPageV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/assessments-lab" element={<AssessmentsLabV3 />} />}
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/assessments-flow" element={<AssessmentsFlowV3 />} />}
            <Route path="/teacher-dashboard/gradebook" element={<GradebookPageV3 />} />
            <Route path="/teacher-dashboard/students" element={<StudentsPageV3 />} />
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/curriculum" element={<CurriculumConnectPageV3 />} />}
            {(process.env.REACT_APP_CURRICULUM_CONNECT === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/curriculum/setup" element={<CurriculumSetupPageV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/curriculum/plc" element={<PlcPlanPageV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/curriculum/intake-lab" element={<CurriculumIntakeLabV3 />} />}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/curriculum/context-lab" element={<ClassContextLabV3 />} />}
            {(process.env.REACT_APP_PLCS === '1' || process.env.NODE_ENV === 'development') && <Route path="/teacher-dashboard/plcs" element={<PlcsPageV3 />} />}
            <Route path="/teacher-dashboard/insights" element={<Navigate to="/teacher-dashboard" replace />} />
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/guide" element={<GuidePageV3 />} />}
            {/* Daily brief's "Show the questions" target — opens in a new tab,
                chrome-less (BRINGS_OWN_CHROME prefix covers it). */}
            <Route path="/teacher-dashboard/missed-questions" element={<MissedQuestionsPageV3 />} />
            {/* Loop lab: isolation build, fake data, delete after Stage A. */}
            {process.env.NODE_ENV === 'development' && <Route path="/teacher-dashboard/loop-lab" element={<LoopLabV3 />} />}
            {/* Old -v3 URLs still work (Sandy 2026-08-09 URL swap): anything
                bookmarked lands on the canonical path. */}
            <Route path="/student-dashboard-v3/*" element={<Navigate to="/student-dashboard" replace />} />
            <Route path="/teacher-dashboard-v3/*" element={<Navigate to="/teacher-dashboard" replace />} />
            <Route path="/student-dashboard" element={<StudentHomeV3 />} />
            {/* MUST precede /:subject or "homework" is read as a subject. */}
            <Route path="/student-dashboard/homework" element={<HomeworkListV3 />} />
            <Route path="/student-dashboard/:subject" element={<StudentSubjectV3 />} />
            <Route path="/student-dashboard/:subject/:domainKey" element={<StudentDomainV3 />} />
            <Route path="/student-homework/:classID/:hwId" element={<HomeworkPlayerV3 />} />
            {/* Adaptive math placement: one walk per domain (claude-placement-api) */}
            <Route path="/placement/:domainKey" element={<PlacementWalkV3 />} />

            <Route path="/admin-dashboard" element={<AdminOverviewV3 />} />
            {/* Overview replaces the retired Groups / Classes page. */}
            <Route path="/admin-dashboard/classes" element={<Navigate to="/admin-dashboard" replace />} />
            <Route path="/admin-dashboard/groups" element={<Navigate to="/admin-dashboard" replace />} />
            <Route path="/admin-dashboard/goals" element={<AdminGoalsV3 />} />
            {process.env.NODE_ENV === 'development' && <Route path="/admin-dashboard/results" element={<AdminResultsV3 />} />}
            <Route path="/admin-dashboard/diagnostics" element={<AdminDiagnosticsV3 />} />
            <Route path="/admin-dashboard/diagnostics/results" element={<AdminDiagnosticResultsV3 />} />
            <Route path="/admin-dashboard/report" element={<AdminReportV3 />} />
            <Route path="/admin-dashboard/guide" element={<AdminGuideV3 />} />
            {/* Directory pages (2026-09-25): every drill level is a URL, so Back works. */}
            <Route path="/admin-dashboard/students" element={<AdminStudentsV3 />} />
            <Route path="/admin-dashboard/students/:studentId" element={<AdminStudentV3 />} />
            <Route path="/admin-dashboard/teachers" element={<AdminTeachersV3 />} />
            <Route path="/admin-dashboard/teachers/:teacherId" element={<AdminTeacherV3 />} />
            <Route path="/admin-dashboard/classes/:classId" element={<AdminClassV3 />} />
            <Route path="/admin-dashboard/levels" element={<AdminLevelsV3 />} />
            <Route path="/admin-dashboard/usage" element={<AdminUsageV3 />} />
            <Route path="/admin-dashboard/accommodations" element={<AdminAccommodationsV3 />} />
            {/* Buffy lives on Home now (2026-09-24); old links land there. */}
            <Route path="/admin-dashboard/buffy" element={<Navigate to="/admin-dashboard" replace />} />
            {/* Old -v3 URLs (bookmarks, stale links) land on the new base path. */}
            <Route path="/admin-dashboard-v3/*" element={<Navigate to="/admin-dashboard" replace />} />
            <Route path="/admin-dashboard-v3" element={<Navigate to="/admin-dashboard" replace />} />

            <Route path="/district-dashboard-v3" element={<DistrictOverviewV3 />} />
            <Route path="/district-dashboard-v3/schools" element={<DistrictSchoolsV3 />} />
            <Route path="/district-dashboard-v3/results" element={<DistrictResultsV3 />} />
            <Route path="/district-dashboard-v3/report" element={<DistrictReportV3 />} />
            <Route path="/district-dashboard-v3/guide" element={<DistrictGuideV3 />} />

            <Route path="/loadingPractice" element={<LoadingSpinnerPage />} />
            {/* Legacy analytics page removed from the bundle (perf pass
                2026-08-17) - orphaned with StudentPracticeDashboard above. */}
            {/* <Route path="/student-dashboard/analytics" element={<StudentDetailedAnalytics />} /> */}
            <Route path="/concept-definitions" element={<ConceptDefOverview />} />

            <Route path="/formative-assessment-practice" element={<FormativeAssessmentPractice />} />
            <Route path="/formative-assessment-results" element={<FormativeAssessmentResults />} />

            <Route path="/internalDashboard" element={<InternalDashboard />} />
            <Route path="/content-workbench" element={<ContentWorkbench />} />

            {/* The ROOT is the TEACHER landing (Sandy 2026-11-03): a Google
                click lands on the classroom pitch; /teachers stays as an alias
                for the root. The student pitch lives at /students (it was the
                root from 2026-08-12). The schools/admin pitch is /for-schools
                (/schools was already taken by the partner-school registration
                page). */}
            <Route path="/" element={<ConnectLanding />} />
            <Route path="/for-schools" element={<SchoolsLanding />} />
            {/* The old preview paths of the landing redos (live 2026-10-28). */}
            <Route path="/for-schools-preview" element={<Navigate to="/for-schools" replace />} />
            <Route path="/students-preview" element={<Navigate to="/students" replace />} />
            <Route path="/testimonials-preview" element={<Navigate to="/testimonials" replace />} />
            <Route path="/contact-preview" element={<Navigate to="/contact" replace />} />
            <Route path="/teachers" element={<ConnectLanding />} />
            <Route path="/teachers/start" element={<TeacherSurvey />} />
            <Route path="/teachers-preview" element={<Navigate to="/teachers" replace />} />
            <Route path="/teachers-preview/start" element={<Navigate to="/teachers/start" replace />} />
            {process.env.NODE_ENV === 'development' && <Route path="/teachers-preview/moments" element={<ConnectMomentGallery />} />}
            <Route path="/join" element={<JoinPage />} />
            <Route path="/join/:code" element={<JoinPage />} />
            {process.env.NODE_ENV === 'development' && <Route path="/videos/:videoId" element={<ExplainerVideoPage />} />}
            <Route path="/students" element={<StudentsPreview />} />
            {/* <Route path="/homepage" element={<Homepage2 />} /> */}

            <Route path="/mathConcepts" element={<ModulesPage subject="math" />} />
            <Route path="/readingConcepts" element={<ModulesPage subject="reading" />} />

            <Route path="/diagnostic-mini-intro" element={<IntroPage />} />
            <Route path="/diagnostic-mini-questions" element={<QuestionPage />} />
            <Route path="/diagnostic-mini-results-page" element={<DiagnosticMiniResultsPage />} />


            {/* <Route path="/AffiliateDashboard" element={<AffiliateProgram />} /> */}
            <Route path="/Friends" element={<FreindsDashboard />} />
            <Route path="/friends/profile/:displayName" element={<FriendProfile />} />

            <Route path="/acceptable-use" element={<PolicyDocument policyType="acceptable-use" />} />
            <Route path="/privacy-policy" element={<PolicyDocument policyType="privacy-policy" />} />
            <Route path="/terms-of-service" element={<PolicyDocument policyType="terms-of-service" />} />

            {/* College Explorer — both pages gate themselves on login. */}
            <Route path="/colleges" element={<CollegeBrowser />} />
            <Route path="/colleges/:unitid" element={<CollegeDetail />} />
            <Route path="/district-scores" element={<DistrictScoresSearch />} />
            <Route path="/district-scores/:districtCode" element={<DistrictScoresPage />} />
            <Route path="/district-scores/:districtCode/demo" element={<DemoTour />} />
            <Route path="/district-scores/:districtCode/:schoolCode" element={<DistrictScoresPage />} />
            <Route path="/district-scores/:districtCode/:schoolCode/demo" element={<DemoTour />} />

            <Route path="/bluebook/upload" element={<SATBluebookUpload />} />
            <Route path="/bluebook/results" element={<SATBluebookResults />} />
            <Route path="/bluebook/psat" element={<PsatScoreReport />} />


            {/* Teacher previews of the legacy student surfaces - routes
                removed from the bundle (perf pass 2026-08-17). Re-enable with
                their imports above. */}
            {/* <Route path="/teacher/student-preview/dashboard" element={<PreviewCurriculumDashboard />} /> */}
            {/* <Route path="/teacher/student-preview/analytics" element={<PreviewStudentDetailedAnalyticsCopy />} /> */}
            {/* <Route path="/teacher/student-preview/assignments" element={<PreviewTeacherAssignments />} /> */}
            {/* <Route path="/teacher/student-preview/diagnostic" element={<PreviewDiagnosticTest />} /> */}
            {/* <Route path="/teacher/student-preview/homework" element={<PreviewHomework />} /> */}
            {/* <Route path="/teacher/student-preview/learning" element={<PreviewLearningPage />} /> */}
            {/* <Route path="/teacher/student-preview/homework-answered" element={<PreviewHomeworkAnswered />} /> */}
            {/* <Route path="/teacher/student-preview/practice-dashboard" element={<PreviewStudentDashboard />} /> */}
            {/* <Route path="/teacher/student-preview/learn-landing-page" element={<PreviewLandingPage />} /> */}

            {/* Catch-all (added 2026-08-17): stale bookmarks and removed
                legacy URLs land on the student landing instead of a blank
                page - the router previously rendered nothing on no match. */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
          </ViewAsRouteGate>
          </Suspense>
      </AppChrome>
      </ViewAsProvider>
    </Router>
  );
}


function App() {

  useEffect(() => {
    // Check for affiliate ID in URL
    const checkForAffiliateId = () => {
      // Get the current URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const affiliateId = urlParams.get('ref');

      // If an affiliate ID is found in the URL
      if (affiliateId) {
        // Check if we already have an affiliate cookie
        const existingAffiliate = getCookie('affiliateId');

        // Only set the cookie if there isn't one already (first-touch attribution)
        if (!existingAffiliate) {
          // Set cookie with a 30-day expiration
          setCookie('affiliateId', affiliateId, 30);
          console.log(`Affiliate ID set: ${affiliateId}`);
        } else {
          console.log(`Existing affiliate ID found: ${existingAffiliate}`);
        }
      }
    };

    // Run the check when the component mounts
    checkForAffiliateId();
  }, []); // Empty dependency array means this runs once on mount


  return (
    <HelmetProvider>
      <AuthProvider>
        <NavbarProvider>
          <AppContent />
        </NavbarProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}


export default App;
